import type { AssignedAgency } from '../../../../auth/agencyAccess';
import { useAgencyBrandAssignments } from '../../../../supply/agency/hooks';
import { useAgencyCampaignContext } from '../../../agencyCampaignContext';
import { useBrandsQuery } from '../../hooks';
import { useBrandsRouteFilters } from '../../useBrandsRouteFilters';
import { buildAgencyByBrandId, pageAgencyBrands } from './brandsTableData';

/**
 * Brand rows for the brands table. On an admin agency details page (`parentAgency` outside an
 * agency workspace) rows come from the agency's brand assignments, filtered and paged locally;
 * otherwise they come from the server-paged brands list.
 */
export function useBrandsTableRows(
  parentAgency: AssignedAgency | undefined,
  providedAssignments: ReadonlyMap<string, AssignedAgency> | undefined,
) {
  const scope = useAgencyCampaignContext();
  const routeFilters = useBrandsRouteFilters();
  // Outside an agency workspace brands use the network agency endpoints.
  const canAdminAgency = !scope;
  const assignments = useAgencyBrandAssignments(canAdminAgency, parentAgency?.id);
  const assignmentItems = assignments.data ?? [];
  const agencyList = Boolean(parentAgency && !scope);
  const brandQuery = useBrandsQuery(routeFilters.queryFilter, !agencyList);
  const brands = agencyList ? assignments : brandQuery;
  const page = agencyList
    ? pageAgencyBrands(assignmentItems, routeFilters.filters)
    : brandQuery.data;

  return {
    routeFilters,
    agencyByBrandId: providedAssignments ?? buildAgencyByBrandId(assignmentItems),
    assignmentsError: canAdminAgency && assignments.isError ? assignments.error : null,
    items: page?.content ?? [],
    totalItems: page?.totalElements ?? 0,
    loading: brands.isLoading,
    failed: brands.isError || assignments.isError,
    refetch: () => {
      void brands.refetch();
    },
  };
}
